import { ChevronDown, ChevronUp, Clock, Pencil, Play, X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  type QueuedPrompt,
  useChatPromptQueueStore,
} from '@/stores/chat-prompt-queue-store';

const LONG_PROMPT_CHARS = 70;

interface ChatPromptQueueStripProps {
  queueKey: string;
  isBusy: boolean;
  canEdit: boolean;
  onResume: () => void;
  // Returns false when no composer is mounted to receive the text, in which
  // case the prompt stays queued.
  onEditItem: (text: string) => boolean;
  className?: string;
}

export function ChatPromptQueueStrip({
  queueKey,
  isBusy,
  canEdit,
  onResume,
  onEditItem,
  className,
}: ChatPromptQueueStripProps) {
  const items = useChatPromptQueueStore((s) => s.queues[queueKey]);
  const held = useChatPromptQueueStore((s) => !!s.heldKeys[queueKey]);
  const remove = useChatPromptQueueStore((s) => s.remove);
  const clear = useChatPromptQueueStore((s) => s.clear);
  const [expanded, setExpanded] = useState(true);
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(new Set());

  if (!items?.length) return null;

  const countLabel = `${items.length} queued`;

  const editItem = (item: QueuedPrompt) => {
    // Remove only once the text has actually landed in the composer.
    if (onEditItem(item.text)) remove(queueKey, item.id);
  };

  const toggleOpen = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // A turn in flight drains the queue by itself; otherwise (a reloaded queue,
  // or a hold after an error/stop/question) the user dispatches the next one.
  const resumeButton = isBusy ? null : (
    <Button
      size='icon'
      variant='ghost'
      className='size-6 shrink-0'
      aria-label={held ? 'Resume queue' : 'Send next queued message'}
      title={held ? 'Resume queue' : 'Send next queued message'}
      onClick={onResume}
    >
      <Play className='size-3.5' />
    </Button>
  );

  let status: string | null = null;
  if (held) status = 'paused';
  else if (isBusy) status = 'sends after this reply';

  return (
    <div
      className={cn(
        'mx-3 flex flex-col rounded-t-xl border border-b-0 border-border/60 bg-accent/60',
        className,
      )}
    >
      {expanded ? (
        <>
          <div className='flex items-center gap-2 pt-2 pr-2 pb-1 pl-3'>
            <Clock className='size-3.5 shrink-0 text-muted-foreground' />
            <span className='text-xs font-medium'>{countLabel}</span>
            {status && (
              <span className='truncate text-xs text-muted-foreground'>
                · {status}
              </span>
            )}
            <div className='ml-auto flex items-center gap-0.5'>
              {resumeButton}
              <Button
                size='sm'
                variant='ghost'
                className='h-6 px-2 text-xs text-muted-foreground'
                onClick={() => clear(queueKey)}
              >
                Clear
              </Button>
              <Button
                size='icon'
                variant='ghost'
                className='size-6 shrink-0 text-muted-foreground'
                aria-label='Collapse queue'
                title='Collapse'
                onClick={() => setExpanded(false)}
              >
                <ChevronDown className='size-3.5' />
              </Button>
            </div>
          </div>
          <ol className='subtle-scrollbar flex max-h-[200px] flex-col gap-px overflow-y-auto px-1 pb-1.5'>
            {items.map((item, index) => (
              <QueuedPromptRow
                key={item.id}
                item={item}
                position={index + 1}
                isOpen={openIds.has(item.id)}
                canEdit={canEdit}
                onToggle={() => toggleOpen(item.id)}
                onEdit={() => editItem(item)}
                onRemove={() => remove(queueKey, item.id)}
              />
            ))}
          </ol>
        </>
      ) : (
        <div className='flex h-10 items-center gap-2.5 pr-2 pl-3'>
          <span className='shrink-0 rounded bg-primary/15 px-1.5 py-0.5 text-[11px] font-medium text-primary'>
            {held ? 'Paused' : 'Up next'}
          </span>
          <span className='min-w-0 flex-1 truncate text-[13px]'>
            {items[0].text}
          </span>
          {resumeButton}
          <Button
            size='sm'
            variant='outline'
            className='h-6 shrink-0 gap-1 rounded-full bg-transparent pr-2 pl-2.5 text-xs font-normal text-muted-foreground'
            aria-label='Expand queue'
            onClick={() => setExpanded(true)}
          >
            {countLabel}
            <ChevronUp className='size-3' />
          </Button>
        </div>
      )}
    </div>
  );
}

interface QueuedPromptRowProps {
  item: QueuedPrompt;
  position: number;
  isOpen: boolean;
  canEdit: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onRemove: () => void;
}

function QueuedPromptRow({
  item,
  position,
  isOpen,
  canEdit,
  onToggle,
  onEdit,
  onRemove,
}: QueuedPromptRowProps) {
  const isLong =
    item.text.length > LONG_PROMPT_CHARS || item.text.includes('\n');

  return (
    <li className='flex items-start gap-2.5 rounded-md py-1 pr-1 pl-2 hover:bg-muted/60'>
      <span className='mt-0.5 grid size-[18px] shrink-0 place-items-center rounded bg-muted text-[11px] font-medium text-muted-foreground'>
        {position}
      </span>
      <button
        type='button'
        onClick={onToggle}
        disabled={!isLong}
        aria-expanded={isLong ? isOpen : undefined}
        className='min-w-0 flex-1 py-px text-left text-[13px] leading-5 disabled:cursor-default'
      >
        {isOpen ? (
          <span className='subtle-scrollbar block max-h-30 overflow-y-auto pr-1.5 whitespace-pre-wrap'>
            {item.text}
          </span>
        ) : (
          <span className='flex min-w-0 items-center gap-1.5'>
            <span className='min-w-0 flex-1 truncate'>{item.text}</span>
            {isLong && (
              <span className='shrink-0 text-xs text-muted-foreground'>
                {item.text.length} chars
              </span>
            )}
          </span>
        )}
      </button>
      <Button
        size='icon'
        variant='ghost'
        className='size-6 shrink-0 text-muted-foreground'
        aria-label='Edit in composer'
        title='Edit'
        disabled={!canEdit}
        onClick={onEdit}
      >
        <Pencil className='size-3' />
      </Button>
      <Button
        size='icon'
        variant='ghost'
        className='size-6 shrink-0 text-muted-foreground'
        aria-label='Remove from queue'
        title='Remove'
        onClick={onRemove}
      >
        <X className='size-3' />
      </Button>
    </li>
  );
}
